import { useEffect, useState } from 'react';
import type { Editor, EditorState } from '@aitomol/core';

/**
 * Subscribes to an editor that lives outside the aitomol React context.
 *
 * The platform's overlay panels are rendered as siblings of `<MoleculeEditor>`
 * (the LightCone skin does not take children), so they cannot use the toolkit's
 * `useEditorSelector`. The store hands out a fresh state object on every change,
 * which is exactly what `useState` needs to trigger a re-render.
 */
export function useEditorSnapshot(editor: Editor | null): EditorState | null {
    const [state, setState] = useState<EditorState | null>(() => editor?.state ?? null);

    useEffect(() => {
        if (!editor) {
            setState(null);
            return;
        }
        setState(editor.state);
        return editor.store.subscribe(() => setState(editor.state));
    }, [editor]);

    return state;
}

/** Re-renders whenever any of the observed editor state changes. */
export function useEditorSelectorValue<T>(editor: Editor | null, select: (state: EditorState) => T): T | null {
    const state = useEditorSnapshot(editor);
    return state ? select(state) : null;
}